// supervisor-outcome-router-done-path.ts
//
// The verified-done branch of supervisor-outcome-router.ts (§6.1
// "done -> proposal-applier -> commit-owner -> ticket done"), split out so
// the router stays under the 200-line budget (code-reviewer 260909-p6-m2
// re-review, Medium). Behaviour is unchanged:
//   1. apply `result.proposed_changes` BEFORE committing, same attempt's
//      transaction — a stale `expected_revision` fails the WHOLE done
//      outcome (ticket NOT done, nothing committed, reason journaled);
//   2. commit via commit-owner; a fail-closed `policy_denied` verdict is
//      journaled and escalated to a human — never retried, never thrown.
// Dogfood F-1 (plan-done-feature-ticket-guards.ts): non-empty
// `proposed_tickets` and a `plan` ticket that leaves no feature work are
// escalated the same way, before anything is committed; so is (06d R2) a plan
// acceptance delegating an open decision to the worker.

import { randomUUID } from "node:crypto";
import type { AutopilotResult } from "./autopilot-result-schema.js";
import { commitTicket, type GitRunner } from "./commit-owner.js";
import type { MissionStore } from "./mission-store.js";
import {
  planDoneRejection,
  proposedTicketsRejectionReason,
} from "./plan-done-feature-ticket-guards.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import type { RetryDecision, RetryState } from "./retry-policy.js";
import { applyProposedChangesForAttempt } from "./supervisor-proposal-application.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";

export interface DonePathInput {
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  manifestPaths: string[];
  missionId: string;
  runId: string;
  ticketId: string;
  ticketType: string;
  ticketScope: string;
  ticketSubject: string;
  ticketStatus: Record<string, string>;
  retryState: RetryState;
  proposedChanges: AutopilotResult["proposed_changes"];
  /** F-1: never applied; non-empty ⇒ escalate (undefined ⇒ treated as `[]`). */
  proposedTickets?: readonly unknown[];
  /** R-25 (D2): DAG policy warnings (rules 3-4) from the proposal applier, plus (review 260930 L5) commit pathspecs that staged nothing — operator line, undefined ⇒ silent. */
  notify?: (line: string) => void;
  secretValues?: string[]; // 06d review L3: masks the R2 reason; undefined ⇒ patterns only
}

export type DonePathResult =
  | { kind: "done"; commitHeadSha: string | undefined }
  | { kind: "retry_decision"; decision: RetryDecision };

/** Journal an `operation_result` receipt with no preceding intent (audit trail for rejections). */
export async function journalRejectionReceipt(
  store: MissionStore,
  outcome: string,
  remoteId?: string,
): Promise<void> {
  const operationId = randomUUID();
  await store.commit([
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        outcome,
        ...(remoteId === undefined ? {} : { remote_id: remoteId }),
      },
    },
  ]);
}

// Both call sites are a post-hoc rejection of an otherwise "done" attempt
// (proposal apply failed, or commit policy denied) — neither is a worker/
// classifier-level ErrorCategory from error-classifier.ts, so "task_failed"
// (its own generic fallback for "an error without a more specific category")
// is the closest fit for RetryDecision.category (added dogfooding P9b so
// this verdict is journalable, not just a free-text `reason`).
export function escalate(
  state: RetryState,
  reason: string,
): Extract<DonePathResult, { kind: "retry_decision" }> {
  const decision: RetryDecision = {
    outcome: "escalate_waiting_human",
    state,
    reason,
    category: "task_failed",
    evidence: reason,
  };
  return { kind: "retry_decision", decision };
}

export async function routeVerifiedDoneOutcome(input: DonePathInput): Promise<DonePathResult> {
  let manifestPaths = input.manifestPaths;
  // M1 lớp hai (code-reviewer 260922 C1). proposal-applier.ts ép ticket về
  // "todo" khi một amend đổi `acceptance[]` — đổi thước đo thì kết luận cũ hết
  // hiệu lực. Guard đó KHÔNG tự cứu được ca ticket bị ép lại chính là ticket
  // đang chạy: `journalAttemptOutcome` thấy route "done" nên commit tiếp
  // `ticket_state_changed -> done` SAU event "todo", và
  // mission-state-reducer.ts ghi đè không điều kiện ⇒ event sau thắng. Guard bị
  // vô hiệu ngay trong tick nó chạy, không test nào đỏ.
  //
  // Đọc THẲNG proposed_changes chứ không so statusMap trước/sau: `ticketStatus`
  // vào đây là projection đầu tick, nơi ticket ĐANG CHẠY vẫn mang "todo"
  // (supervisor-ticket-projection.ts:35), nên cả "đọc giá trị tuyệt đối" lẫn
  // "so trước/sau" đều sai — cái trước bắn cho MỌI attempt có proposed_changes
  // (e2e cancel-proposal rơi từ done xuống blocked), cái sau không bắn bao giờ.
  //
  // Escalate chứ không retry: worker vừa đổi thước đo ngay lúc tuyên đã đạt —
  // cho chạy tiếp bằng thước mới là để bên bị kiểm tự viết luật.
  const selfAmendsAcceptance = input.proposedChanges.some(
    (change) =>
      change.op === "amend" &&
      change.target === input.ticketId &&
      (change.content as { acceptance?: unknown } | undefined)?.acceptance !== undefined,
  );
  if (selfAmendsAcceptance) {
    await journalRejectionReceipt(input.store, "self_amended_acceptance_rejected");
    return escalate(
      input.retryState,
      `attempt sửa acceptance của chính ticket ${input.ticketId} trong lượt báo done — ` +
        "điều kiện nghiệm thu đổi thì kết luận done không còn hiệu lực, cần người duyệt.",
    );
  }
  const ticketsReason = proposedTicketsRejectionReason(input.ticketId, input.proposedTickets ?? []);
  if (ticketsReason !== undefined) {
    await journalRejectionReceipt(input.store, "proposed_tickets_rejected");
    return escalate(input.retryState, ticketsReason);
  }
  // F-1 R2 on the PROJECTED graph, before anything is applied (review 261010 M2).
  const planRejection =
    input.ticketType === "plan"
      ? planDoneRejection(input, await loadTicketsFromRepo(input.repoDir))
      : undefined;
  if (planRejection !== undefined) {
    await journalRejectionReceipt(input.store, planRejection.receipt);
    return escalate(input.retryState, planRejection.reason);
  }
  if (input.proposedChanges.length > 0) {
    const applied = await applyProposedChangesForAttempt({
      store: input.store,
      repoDir: input.repoDir,
      runId: input.runId,
      ticketStatus: input.ticketStatus,
      proposedChanges: input.proposedChanges,
      sourceTicketId: input.ticketId, // 06e R3: a controller redirect decision may supersede a blocked plan
      notify: input.notify,
    });
    if (!applied.ok) {
      await journalRejectionReceipt(input.store, "proposal_apply_rejected", applied.reason);
      return escalate(
        input.retryState,
        `proposed_changes áp dụng thất bại, ticket KHÔNG được coi là done: ${applied.reason}`,
      );
    }
    if (applied.touchedTicketIds.length > 0) {
      manifestPaths = [...input.manifestPaths, ".avatar/tickets", ".avatar/PLAN.md"]; // 06e R5
    }
  }

  const commit = await commitTicket({
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths,
    missionId: input.missionId,
    runId: input.runId,
    ticketId: input.ticketId,
    type: input.ticketType,
    scope: input.ticketScope,
    subject: input.ticketSubject,
    notify: input.notify,
  });
  if (commit.status === "policy_denied") {
    // Governance denial on the supervisor's OWN commit (§5.4): not a worker
    // error category, so counted_failures does not apply — a human decides.
    await journalRejectionReceipt(input.store, "policy_denied");
    return escalate(input.retryState, `commit bị policy từ chối: ${commit.reason}`);
  }
  return {
    kind: "done",
    commitHeadSha: commit.status === "committed" ? commit.headSha : commit.baseSha,
  };
}
